'use es6';

import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H4 from 'UIComponents/elements/headings/H4';
import UIFieldset from 'UIComponents/form/UIFieldset';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIExpandingTextArea from 'UIComponents/input/UIExpandingTextArea';
import LazyLoadingInput from '../LazyLoadingInput';
import { KeyTypes, PopoverType } from '../../constants/popover';
import { validateEmbedHtml, sanitizeEmbedHtml, applyLazyLoadingToEmbed, extractLoadingValue } from '../../utils/embedSrc';
import ExplicitSaveControls from '../ExplicitSaveControls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = suffix => `RichTextLib.embed.popoverBody.${suffix}`;
const propTypes = {
  tooltipClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  embed: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
};
function EmbedPopoverBodyEdit(props) {
  const {
    tooltipClassName,
    dropdownClassName,
    embed: initialEmbedCode,
    onSubmit,
    onRemove,
    onClose
  } = props;
  const [embedCode, setEmbedCode] = useState(initialEmbedCode);
  const [hasErrors, setHasErrors] = useState(false);
  const [loadingValue, setLoadingValue] = useState(extractLoadingValue(initialEmbedCode));
  const embedInputRef = useRef(null);
  useEffect(() => {
    setEmbedCode(initialEmbedCode);
  }, [initialEmbedCode]);
  const handleCloseOnEscape = evt => {
    if (evt.key === KeyTypes.ESCAPE) {
      onClose();
    }
  };
  const handleChange = evt => {
    const newEmbedCode = evt.target.value;
    setEmbedCode(newEmbedCode);
    setHasErrors(false);
    setLoadingValue(extractLoadingValue(newEmbedCode));
  };
  const handleLoadingValueChange = evt => {
    const {
      value
    } = evt.target;
    const embedCodeWithLoading = applyLazyLoadingToEmbed(embedCode, value);
    handleChange({
      target: {
        value: embedCodeWithLoading
      }
    });
  };
  const handleSubmit = () => {
    const embed = sanitizeEmbedHtml(embedCode);
    if (validateEmbedHtml(embed)) {
      onRemove();
      onSubmit({
        embed
      });
    } else {
      setHasErrors(true);
      embedInputRef.current.focus();
    }
  };
  const validationMessage = hasErrors ? I18n.text(i18nKey('embedCodeError')) : '';
  return /*#__PURE__*/_jsxs(UIFieldset, {
    className: "small-popover-body p-all-5",
    _size: "small",
    tabIndex: "-1",
    onKeyDown: handleCloseOnEscape,
    children: [/*#__PURE__*/_jsx(H4, {
      className: "m-bottom-2",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('title')
      })
    }), /*#__PURE__*/_jsx(UIFormControl, {
      htmlFor: "embed_txtarea",
      label: I18n.text(i18nKey('embedCodeLabel')),
      tooltip: I18n.text(i18nKey('embedCodeHelp')),
      tooltipPlacement: "top right",
      tooltipProps: {
        innerClassName: tooltipClassName
      },
      error: hasErrors,
      validationMessage: validationMessage,
      children: /*#__PURE__*/_jsx(UIExpandingTextArea, {
        name: "embed_txtarea",
        "data-gramm": false // Disable Grammarly
        ,
        defaultValue: initialEmbedCode,
        inputRef: embedInputRef,
        minHeight: 180,
        maxHeight: 180,
        shrink: true,
        value: embedCode,
        onChange: handleChange
      })
    }), loadingValue && /*#__PURE__*/_jsx(LazyLoadingInput, {
      tooltipClassName: tooltipClassName,
      dropdownClassName: dropdownClassName,
      onChange: handleLoadingValueChange,
      value: loadingValue,
      label: "embedLabel"
    }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
      className: "m-top-5",
      popoverType: PopoverType.EDIT,
      isSubmitDisabled: embedCode === initialEmbedCode,
      onSubmit: handleSubmit,
      onCancel: onClose
    })]
  });
}
EmbedPopoverBodyEdit.propTypes = propTypes;
export default EmbedPopoverBodyEdit;